import React, { useMemo, useCallback, useImperativeHandle, useEffect, useState, useRef, useContext } from 'react';
import Form, { ButtonItem, EmptyItem, FormRef, GroupItem, Item, Label, SimpleItem } from 'devextreme-react/form';
import { AssistitiApi, OperatoriApi, PuntiDiRaccoltaApi } from '../../data';
import { apiConfiguration } from '../../apiConfig';
import { FieldDataChangedEvent } from 'devextreme/ui/form';
import { ClickEvent } from 'devextreme/ui/button';
import { LoadPanel, Lookup } from 'devextreme-react';
import TenantContext from '../../contexts/tenantContext';
import { on } from 'events';
import { showError } from '../../utils/errorHandling';
import ComuneSelector, { ILocation } from '../comuneSelector/ComuneSelector';
import notify from 'devextreme/ui/notify';
import { isUndefined } from 'util';
import { useEntiDataSource } from '../../datasources/EntiDataSource';
import PositionEditPopup, { PositionEditPopupHandle } from '../mapPanel/PositionEditPopup';
// import { useComuniDataSource } from '../../datasources/ComuniDataSource'; //todo: RGAM non esiste più datasource comuni.

type Props = {
  puntoDiRaccoltaId?: string;
  onchanged?: () => void;
}

export type PuntoDiRaccoltaEditPanelHandle = {
  save: () => Promise<boolean>;
}

type state = {
  isNew: boolean
}

const PuntoDiRaccoltaEditPanel: React.ForwardRefRenderFunction<PuntoDiRaccoltaEditPanelHandle, Props> = (props, ref) => {
  const { currentTenant } = useContext(TenantContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [formData, setFormData] = useState<any>();
  const formRef = useRef<FormRef>(null);
  const positionEditPopupRef = useRef<PositionEditPopupHandle>(null);
  const entiDataSource = useEntiDataSource();

  useEffect(() => {
    setFormData(undefined);

    (async function () {
      const api = new PuntiDiRaccoltaApi(apiConfiguration);
      setLoading(true);

      var data;
      if (props.puntoDiRaccoltaId) {
        setFormData(await api.getPuntoDiRaccoltaById({ tenantId: currentTenant.Id, puntoDiRaccoltaId: props.puntoDiRaccoltaId }));
      }
      else {
        setFormData(await api.getPuntoDiRaccoltaDefault({ tenantId: currentTenant.Id })); 
      }

      setLoading(false);
    })();

  }, [props.puntoDiRaccoltaId]);

  const isEnteDisabled = useMemo(() => !formData?.id, [props.puntoDiRaccoltaId]);

  useImperativeHandle(ref, () => ({
    async save() {
      if (saving) {
        return false; // already saving
      }

      var validate = formRef.current?.instance().validate();

      if (validate?.isValid) {
        const api = new PuntiDiRaccoltaApi(apiConfiguration);

        var data = formRef.current?.instance().option("formData");

        setSaving(true);

        try {
          if (props.puntoDiRaccoltaId) {
            await api.updatePuntoDiRaccolta({
              tenantId: currentTenant.Id,
              puntoDiRaccoltaId: props.puntoDiRaccoltaId,
              puntoDiRaccoltaUpdateDTO: data
            });

            props.onchanged?.();
          }
          else {
            await api.insertPuntoDiRaccolta({
              tenantId: currentTenant.Id,
              puntoDiRaccoltaCreateDTO: data
            });

            props.onchanged?.();
          }

          return true;
        }
        catch (e) {
          showError(e);
          return false;
        }
        finally {
          setSaving(false);
        }

      }
      else {
        notify("Correggere gli errori di validazione", "error", 2000);
        return false;
      }
    }
  }));

  const mapButtonClick = (e: ClickEvent) => {
    var data = formRef.current?.instance().option("formData");
    const address=`${data?.indirizzo ?? ''} ${data?.numeroCivico ?? ''}, ${data?.comune ?? ''}, ${data?.provincia ?? ''}, ${data?.nazioneId ?? ''}`;

    positionEditPopupRef.current?.edit(formData?.latitudine, formData?.longitudine, address);
  };

  return (
    <React.Fragment>

      {!loading &&
        <Form id="form" ref={formRef} colCount={3} formData={formData} labelMode='floating' onFieldDataChanged={props.onchanged} >

          <Item dataField="nome" isRequired={true} colSpan={3} />

          <GroupItem caption="Localizzazione" colSpan={3} colCount={4}>
            <SimpleItem
              colSpan={4}
              render={(e) =>
                <ComuneSelector
                  data={{
                    nazioneId: formData?.nazioneId,
                    regioneId: formData?.regioneId,
                    provinciaId: formData?.provinciaId,
                    comuneId: formData?.comuneId,
                    regione: formData?.regione,
                    provincia: formData?.provincia,
                    comune: formData?.comune
                  }}
                  onValueChanged={(data) => {
                    e?.component.updateData("comuneId", data.comuneId);
                    e?.component.updateData("provinciaId", data.provinciaId);
                    e?.component.updateData("regioneId", data.regioneId);
                    e?.component.updateData("nazioneId", data.nazioneId);
                    e?.component.updateData("comune", data.comune);
                    e?.component.updateData("provincia", data.provincia);
                    e?.component.updateData("regione", data.regione);
                  }}
                />
              }
            />
            <Item dataField="localita" caption='Località' />
            <Item dataField="indirizzo" colSpan={2} />
            <GroupItem colSpan={1} colCount={2}>
<Item dataField="numeroCivico" />
<Item dataField="cap" />
            </GroupItem>
            

            <GroupItem colSpan={2} colCount={3}>
              <Item dataField="latitudine" editorType="dxNumberBox" />
              <Item dataField="longitudine" editorType="dxNumberBox" />
              <ButtonItem horizontalAlignment='left' verticalAlignment='bottom' buttonOptions={{
                icon: "pinmap",
                hint: "Posiziona sulla mappa",
                onClick: mapButtonClick
              }}></ButtonItem>
            </GroupItem>
            <SimpleItem dataField="informazioniDiAccesso" colSpan={2} editorType="dxTextArea" editorOptions={{ height: 80 }} />
          </GroupItem>

          <GroupItem caption="Contatti" colSpan={3} colCount={3}>
            <Item dataField="email" />
            <Item dataField="telefono" />
            <Item dataField="cellulare" />
          </GroupItem>

          {/* <GroupItem caption="Altro" colSpan={3} colCount={3}>
            <Item dataField="capienzaMassima" editorType="dxNumberBox" />
          </GroupItem> */}

          <SimpleItem dataField="note" colSpan={3} editorType="dxTextArea" editorOptions={{ height: 80 }} />

        </Form>
      }
      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        position={{ of: '#form' }}
        visible={loading}
        showIndicator={true}
        shading={false}
        showPane={true}
        hideOnOutsideClick={false}
      />

      <PositionEditPopup ref={positionEditPopupRef} onCoordinateChange={(lat, lon) => {
        formRef.current?.instance()?.updateData("latitudine", lat);
        formRef.current?.instance()?.updateData("longitudine", lon);
      }} />
    </React.Fragment>
  );
}

export default React.forwardRef(PuntoDiRaccoltaEditPanel);